import type { ActivityContext, ActivityHandle } from '../../core/activity';
import { makeDraggable } from '../../shared/drag-drop';
import levelsData from './levels.json';

interface RoundConfig {
  rounds: number;
  numbersPerRound: number;
  minValue: number;
  maxValue: number;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: RoundConfig[];
}

interface LevelsFile {
  levels: Level[];
}

type Parity = 'par' | 'impar';

interface NumberItem {
  value: number;
  placed: Parity | null;
  state: 'pending' | 'correct' | 'wrong';
}

const LEVELS = (levelsData as LevelsFile).levels;

export function mountNumbersOddEven(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  let roundIndex = 0;
  let numbers: NumberItem[] = [];
  let selectedIdx: number | null = null;
  let correctRoundsCount = 0;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Pares e ímpares</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage odd-even">
      <div class="feedback" aria-live="polite"></div>
      <p class="odd-even__progress" aria-live="polite"></p>
      <p class="odd-even__hint">Toque num número e depois na caixa certa.</p>
      <div class="odd-even__pool" role="list" aria-label="Números pra classificar"></div>
      <div class="odd-even__boxes">
        <div class="odd-even__box odd-even__box--par" role="button" tabindex="0" data-parity="par" aria-label="Colocar na caixa de pares">
          <span class="odd-even__box-label">PAR</span>
          <div class="odd-even__box-items"></div>
        </div>
        <div class="odd-even__box odd-even__box--impar" role="button" tabindex="0" data-parity="impar" aria-label="Colocar na caixa de ímpares">
          <span class="odd-even__box-label">ÍMPAR</span>
          <div class="odd-even__box-items"></div>
        </div>
      </div>
      <button class="btn btn--primary odd-even__ok" type="button">Confirmar</button>
    </section>
  `;
  ctx.root.appendChild(page);

  const poolEl = page.querySelector<HTMLElement>('.odd-even__pool')!;
  const parBoxItemsEl = page.querySelector<HTMLElement>('.odd-even__box--par .odd-even__box-items')!;
  const imparBoxItemsEl = page.querySelector<HTMLElement>('.odd-even__box--impar .odd-even__box-items')!;
  const progressEl = page.querySelector<HTMLElement>('.odd-even__progress')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.odd-even__ok')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  function startRound(): void {
    const cfg = level.data[0]!;
    numbers = [];
    const seen = new Set<number>();
    while (numbers.length < cfg.numbersPerRound) {
      const v = randInt(cfg.minValue, cfg.maxValue);
      if (seen.has(v)) continue;
      seen.add(v);
      numbers.push({ value: v, placed: null, state: 'pending' });
    }
    selectedIdx = null;
    renderProgress();
    renderAll();
  }

  function renderProgress(): void {
    const total = level.data[0]?.rounds ?? 1;
    progressEl.textContent = `Rodada ${roundIndex + 1} de ${total}`;
  }

  function renderAll(): void {
    renderPool();
    renderBox('par');
    renderBox('impar');
  }

  function renderPool(): void {
    poolEl.innerHTML = '';
    numbers.forEach((n, i) => {
      if (n.placed !== null) return;
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'odd-even__num';
      el.dataset.idx = String(i);
      el.textContent = String(n.value);
      if (i === selectedIdx) el.classList.add('odd-even__num--selected');
      el.addEventListener('click', () => clickNumber(i));
      poolEl.appendChild(el);
      makeDraggable(el, {
        getDropTarget: (x, y) => {
          const e = document.elementFromPoint(x, y);
          return e?.closest<HTMLElement>('.odd-even__box') ?? null;
        },
        onDrop: (targetEl) => {
          const parity = targetEl.dataset['parity'] as Parity | undefined;
          if (!parity) return;
          selectedIdx = i;
          placeIntoBox(parity);
        },
      });
    });
  }

  function renderBox(parity: Parity): void {
    const target = parity === 'par' ? parBoxItemsEl : imparBoxItemsEl;
    target.innerHTML = '';
    numbers.forEach((n, i) => {
      if (n.placed !== parity) return;
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'odd-even__chip';
      el.dataset.idx = String(i);
      el.textContent = String(n.value);
      if (n.state === 'correct') el.classList.add('odd-even__chip--correct');
      if (n.state === 'wrong') el.classList.add('odd-even__chip--wrong');
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        removeFromBox(i);
      });
      target.appendChild(el);
    });
  }

  function clickNumber(idx: number): void {
    if (selectedIdx === idx) {
      selectedIdx = null;
    } else {
      selectedIdx = idx;
      ctx.audio.play('click');
    }
    renderPool();
  }

  function placeIntoBox(parity: Parity): void {
    if (selectedIdx === null) return;
    const n = numbers[selectedIdx]!;
    n.placed = parity;
    n.state = 'pending';
    selectedIdx = null;
    ctx.audio.play('click');
    renderAll();
  }

  function removeFromBox(idx: number): void {
    const n = numbers[idx]!;
    if (n.placed === null) return;
    n.placed = null;
    n.state = 'pending';
    ctx.audio.play('click');
    renderAll();
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 600);
  }

  function validate(): void {
    if (numbers.some((n) => n.placed === null)) {
      showFeedback('err');
      ctx.audio.play('wrong');
      return;
    }
    let anyWrong = false;
    numbers.forEach((n) => {
      const expected: Parity = n.value % 2 === 0 ? 'par' : 'impar';
      if (n.placed === expected) n.state = 'correct';
      else { n.state = 'wrong'; anyWrong = true; }
    });
    if (!anyWrong) {
      ctx.audio.play('correct');
      showFeedback('ok');
      correctRoundsCount++;
      roundIndex++;
      renderAll();
      const totalRounds = level.data[0]?.rounds ?? 1;
      window.setTimeout(() => {
        if (roundIndex < totalRounds) startRound();
        else finishLevel();
      }, 800);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      renderAll();
    }
  }

  okBtn.addEventListener('click', validate);
  page.querySelectorAll<HTMLElement>('.odd-even__box').forEach((box) => {
    box.addEventListener('click', () => placeIntoBox(box.dataset.parity as Parity));
    box.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        placeIntoBox(box.dataset.parity as Parity);
        e.preventDefault();
      }
    });
  });

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      validate();
      e.preventDefault();
    } else if (e.key === 'p' || e.key === 'P') {
      placeIntoBox('par');
      e.preventDefault();
    } else if (e.key === 'i' || e.key === 'I') {
      placeIntoBox('impar');
      e.preventDefault();
    }
  };
  window.addEventListener('keydown', onKey);

  function finishLevel(): void {
    ctx.audio.play('win');
    const totalRounds = level.data[0]?.rounds ?? 1;
    ctx.onComplete(level.level, correctRoundsCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctRoundsCount} de ${totalRounds} ${totalRounds === 1 ? 'rodada' : 'rodadas'}.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/numbers-odd-even/${level.level + 1}`;
      });
    }
  }

  startRound();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}

function randInt(min: number, max: number): number {
  if (max < min) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
